import classNames from 'classnames';
import * as styles from './Reader.module.scss';
import { useEffect, useRef, useState } from 'react';
import { readBarcodes } from 'zxing-wasm';

export const Reader = ({ onScan, deviceId }) => {
    const isMountedRef = useRef(true);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const animationFrameRef = useRef(null);
    const processingRef = useRef(false);
    const restartTimeoutRef = useRef(null);
    const streamRef = useRef(null);
    const rectRef = useRef(null);
    const containerRef = useRef(null);

    const [loading, setLoading] = useState(false);

    const clearScanner = () => {
        clearTimeout(restartTimeoutRef.current);
        restartTimeoutRef.current = null;

        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;

        // Останавливаем все треки медиапотока
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        if (videoRef.current?.srcObject) {
            videoRef.current.srcObject = null;
        }
    };

    const startScanner = async (selectedDeviceId, retry) => {
        if (!isMountedRef.current) return;

        setLoading(true);
        clearScanner();

        await new Promise(res => setTimeout(res, 100));

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: selectedDeviceId ? { deviceId: selectedDeviceId } : { facingMode: 'environment' },
                audio: false
            });

            if (!isMountedRef.current) {
                stream.getTracks().forEach(track => track.stop());
                return;
            }

            clearTimeout(restartTimeoutRef.current);

            let flag = false
            stream.getTracks().forEach(track => {
                if (track.readyState === 'ended') flag = true;
            });
            if (flag) {
                return startScanner(deviceId, retry);
            }

            streamRef.current = stream;

            if (videoRef.current) {
                videoRef.current.srcObject = stream;

                await videoRef.current.play();

                startProcessing();
            }
        } catch (error) {
            if (retry > 6) return updateError(error.toString());
            if (!isMountedRef.current) return;

            clearTimeout(restartTimeoutRef.current);
            restartTimeoutRef.current = setTimeout(() => startScanner(selectedDeviceId, retry + 1), 400);
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    };

    const restartScanner = async (selectedDeviceId) => {
        if (loading && !selectedDeviceId) return;
        await startScanner(selectedDeviceId, 0);
    };

    // Обработка кадров
    const processFrame = async () => {
        if (!isMountedRef.current || !videoRef.current || !canvasRef.current || processingRef.current) return;

        processingRef.current = true;

        try {
            const ctx = canvasRef.current.getContext('2d', { willReadFrequently: true });
            const video = videoRef.current;

            canvasRef.current.width = video.videoWidth;
            canvasRef.current.height = video.videoHeight;

            ctx.drawImage(video, 0, 0);

            if (!canvasRef.current.width || !canvasRef.current.height) {
                clearTimeout(restartTimeoutRef.current);
                return restartScanner(deviceId);
            }

            if (window.AndroidScanner) {
                const base64Image = canvasRef.current.toDataURL('image/png');
                window.AndroidScanner.processImage(base64Image);
            } else {
                const imageData = ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height);

                const result = await readBarcodes(imageData, {
                    tryHarder: true,
                    formats: ['QRCode'],
                });

                if (result?.length > 0) {
                    const pos = result[0].position
                    const left = Math.min(pos.topLeft.x, pos.bottomLeft.x);
                    const top = Math.min(pos.topLeft.y, pos.topRight.y);
                    const right = Math.max(pos.topRight.x, pos.bottomRight.x);
                    const bottom = Math.max(pos.bottomLeft.y, pos.bottomRight.y);

                    displayRect([left, top, right, bottom]);

                    onScan({ result: result[0] });
                }
            }
        } catch (error) {
            throw error
        } finally {
            processingRef.current = false;
            if (isMountedRef.current) {
                animationFrameRef.current = requestAnimationFrame(processFrame);
            }
        }
    };

    const startProcessing = () => {
        if (!isMountedRef.current) return;
        animationFrameRef.current = requestAnimationFrame(processFrame);
    };
    const displayRect = (coords) => {
        if (!containerRef.current) return;
        if (!coords || coords.length < 4) return;

        const [left, top, right, bottom] = coords;

        const video = videoRef.current;
        const container = containerRef.current;
        const videoRect = container.getBoundingClientRect();
        const scaleX = videoRect.width / video.videoWidth;
        const scaleY = videoRect.height / video.videoHeight;

        const style = {
            left: left * scaleX + 'px',
            top: top * scaleY + 'px',
            width: (right - left) * scaleX + 'px',
            height: (bottom - top) * scaleY + 'px',
        };

        clearRect();
        setTimeout(clearRect, 200);

        rectRef.current = document.createElement('div');
        rectRef.current.className = styles.codeRect;
        container.appendChild(rectRef.current);

        Object.assign(rectRef.current.style, style);
    };
    const clearRect = () => {
        if (rectRef.current) {
            rectRef.current.remove();
            rectRef.current = null;
        }
    };

    useEffect(() => {
        const androidScanHandler = (event) => {
            const result = event.detail;
            if (result.rawValue) {
                if (result.boundingBox && containerRef.current) {
                    const coords = result.boundingBox.match(/[\d\.]+/g);

                    if (!coords) return;

                    displayRect(coords.map(parseFloat))
                }

                onScan({ result: { text: result.rawValue } });
            }
        }
        const handleResize = () => {
            if (rectRef.current) {
                const rect = JSON.parse(rectRef.current.dataset.rect);
                displayRect(rect);
            }
        };

        window.addEventListener('barcode-scanned', androidScanHandler);
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('barcode-scanned', androidScanHandler);
            window.removeEventListener('resize', handleResize);

            isMountedRef.current = false;
            clearScanner();
            clearRect();
        };
    }, []);

    useEffect(() => {
        if (deviceId) {
            clearTimeout(restartTimeoutRef.current);
            restartScanner(deviceId);
        }
    }, [deviceId]);

    return (
        <div className={classNames(styles.reader, 'uikit_reader')} ref={containerRef}>
            <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{ width: '100%', height: 'auto' }}
            />
            <canvas
                ref={canvasRef}
                style={{ display: 'none' }}
            />
        </div>
    );
};